/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { useState } from 'react'

import { SERVER_ERROR_MESSAGE } from '@core/lib/utils'
import useCatalogStore from '@core/stores/catalog/useCatalogStore'
import useBackdropLoaderStore from '@core/stores/loader/useBackdropLoaderStore'
import useMiniAlertStore from '@core/stores/miniAlert/useMiniAlertStore'
import useRegisterStore from '@core/stores/register/useRegisterStore'
import { type RegisterResData } from '@core/types/server/registerRes'
import { sendEventGA } from '@services/analytics'
import {
  getUserInfoFromRENIEC,
  type RegisterBody,
  registerUser,
  verifyIfUserExists
} from '@services/register'

import { type DNIFormData } from '../DNIForm/DNISchema'
import { type RegisterFormData } from '../RegisterForm/registerSchema'

export const useRegister = (
  onUserExists: (data: DNIFormData) => Promise<void> | void,
  afterRegister: (data: RegisterResData, documentType: number) => Promise<void> | void,
  onUserDontExists?: () => Promise<void> | void
) => {
  const { openAlert } = useMiniAlertStore()
  const { sexsCatalog, documentTypesCatalog } = useCatalogStore()
  const { closeLoader, openLoader, isLoading, loadingMessage } = useBackdropLoaderStore()
  const { setUserInfo, setDocument, setLockInputs, resetState } = useRegisterStore()

  const [showCompleteRegisterForm, setShowCompleteRegisterForm] = useState(false)

  const onRegister = async (data: RegisterFormData) => {
    if (!sexsCatalog || !documentTypesCatalog) {
      throw new Error('Falta informacion para el registro')
    }

    const gender = sexsCatalog.find(doc => doc.id === data.sex)
    const documentType = documentTypesCatalog.find(doc => doc.id === data.documentType)
    if (!documentType || !gender || !data.born_date) {
      throw new Error('No llegaron todos los datos para registrar el familiar')
    }

    const registerUserBody: RegisterBody = {
      centerId: 1,
      email: data.email.trim(),
      emailConfirm: data.email.trim(),
      password: data.password,
      name: data.name,
      surname1: data.lastname1,
      surname2: data.lastname2,
      birthdateAsString: data.born_date,
      gender,
      documentType,
      username: data.documentNumber.trim(),
      documentNumber: data.documentNumber.trim(),
      termsAccepted: data.term_conditions,
      phone: data.phone_number.trim()
    }

    openLoader('Creando tu cuenta...')

    sendEventGA('sign_up', {
      hash_doc: registerUserBody.documentNumber.trim(),
      type_doc: registerUserBody.documentType.name,
      fecha_nacimiento: registerUserBody.birthdateAsString,
      genero: registerUserBody.gender.name,
      hash_email: registerUserBody.email.trim(),
      hash_celular: registerUserBody.phone.trim(),
      method: 'normal'
    })

    try {
      const userRegistered = await registerUser(registerUserBody)
      // eslint-disable-next-line prettier/prettier, no-console
      console.log({ userRegistered })
      resetState()
      afterRegister(userRegistered, data.documentType)
    } catch (error: any) {
      console.error(error)
      openAlert(error.message as string)
    } finally {
      closeLoader()
    }
  }

  const onSubmitDNIForm = async (data: DNIFormData) => {
    const { documentNumber, documentType } = data
    if (!documentNumber || !documentType) return

    openLoader('Estamos validando tus datos...')
    const centerId2 = 1
    // ? En primer lugar, se consulta si existe el usuario
    const userExist = await verifyIfUserExists(centerId2, documentType, documentNumber)

    if (userExist === null) {
      openAlert(SERVER_ERROR_MESSAGE)

      return
    }

    // ? Caso 1: usuario existe
    if (userExist) {
      await onUserExists(data)
      closeLoader()

      return
    }

    // ? Caso 2: Usuario no existe y es otro tipo de documento que NO sea DNI
    // ? En este caso no se valida el documento y se pasa a la siguiente vista
    if (documentType !== 1) {
      setDocument({ number: documentNumber, type: documentType })
      setLockInputs(false)
      closeLoader()
      setShowCompleteRegisterForm(true)
      onUserDontExists?.()

      return
    }

    // ? Caso 3: Usuario no existe y el tipo de documento es DNI
    // ? Se realiza la consulta a RENIEC y se llena el formulario de manera automatizada
    try {
      const reniecData = await getUserInfoFromRENIEC(data.documentNumber)
      setDocument({ number: documentNumber, type: documentType })
      setUserInfo(reniecData)
      if (reniecData.success) setLockInputs(true)
      else setLockInputs(false)
      setShowCompleteRegisterForm(true)
      onUserDontExists?.()
    } catch (error: any) {
      throw new Error(error.message as string)
    } finally {
      closeLoader()
    }
  }

  const onClickBack = () => {
    resetState()
    setShowCompleteRegisterForm(false)
  }

  return {
    showCompleteRegisterForm,
    loading: isLoading,
    loadingMessage,

    onClickBack,
    onSubmitDNIForm,
    onRegister
  }
}
